import React, { ReactNode } from 'react';
import { Badge, type CustomFlowbiteTheme } from 'flowbite-react';
import { IconLoader, type IconName, type IconWithAction } from '../iconLoader';
import { Text, TextProps } from '../text';
import { badgeTheme, compactBadgeTheme } from '../../styles/themes/tag';
import { ComponentSize } from '../select';

export type TagThemeColor = 'primary' | 'error' | 'warning' | 'success' | 'secondary' | 'information';
export type TagSize = Extract<ComponentSize, 'sm' | 'md'>;
/** 'compact' is the dense status-badge treatment: 16px tall, 4px radius, uppercase label */
export type TagVariant = 'default' | 'compact';

export const iconThemeClasses: Record<TagThemeColor, string> = {
    primary: 'fill-system-on-surface',
    error: 'fill-system-on-error',
    warning: 'fill-system-on-warning-container',
    success: 'fill-system-on-success',
    secondary: 'fill-system-on-secondary',
    information: 'fill-system-on-information',
};

const labelVariants: Record<TagSize, TextProps['variant']> = {
    sm: 'label-small',
    md: 'label-medium',
};

export type TagProps = {
    id?: string;
    testId?: string;
    label?: string;
    color?: TagThemeColor;
    size?: TagSize;
    variant?: TagVariant;
    href?: string;
    handleClick?: () => void;
    leftIcon?: IconWithAction;
    rightIcon?: IconWithAction;
    className?: string;
    leftComponent?: { element: ReactNode; condition: boolean };
    hoverable?: boolean;
    /** Renders the tag inside a real `<button>` for `handleClick`; icon actions and `href` are then ignored. */
    asButton?: boolean;
    /** Button mode only: makes the tag a toggle, exposed as `aria-pressed`. */
    pressed?: boolean;
    /** Button mode only: a fuller accessible name, which should contain the visible label. */
    ariaLabel?: string;
};

const tagButtonClass =
    'inline-flex appearance-none border-0 bg-transparent p-0 m-0 rounded-md cursor-pointer ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-system-primary';

type TagIconProps = {
    icon?: IconWithAction;
    color: TagThemeColor;
    className?: string;
    /** Drops the icon's action, e.g. inside a tag button, which cannot contain another control. */
    decorative?: boolean;
};

const iconButtonClass =
    'inline-flex shrink-0 items-center p-0 border-none bg-transparent cursor-pointer rounded-sm ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-primary';

// An icon with an action is a named button, so it is reachable and operable from the keyboard.
const TagIcon: React.FC<TagIconProps> = ({ icon, color, className, decorative = false }) => {
    if (!icon) {
        return null;
    }
    const { actionLabel, testId, iconName, size, className: iconClassName } = icon;
    const action = decorative ? undefined : icon.action;
    const iconElement = (
        <IconLoader
            className={`${iconClassName || iconThemeClasses[color]} shrink-0${
                !action && className ? ` ${className}` : ''
            }`}
            iconName={iconName as IconName}
            size={size ?? 'sm'}
            aria-hidden="true"
            data-testid={testId}
        />
    );
    return action ? (
        <button
            type="button"
            aria-label={actionLabel}
            className={`${iconButtonClass}${className ? ` ${className}` : ''}`}
            onClick={action}
        >
            {iconElement}
        </button>
    ) : (
        iconElement
    );
};

// The left icon renders in the label row rather than Badge's icon slot, which forces aria-hidden on it.
const leftIconTheme = (theme: CustomFlowbiteTheme['badge']): CustomFlowbiteTheme['badge'] => ({
    ...theme,
    // Keep the padding Badge applies when its icon slot is filled.
    icon: { ...theme?.icon, off: theme?.icon?.on },
});

export const Tag: React.FC<TagProps> = (props) => {
    const {
        id,
        testId,
        label = '',
        color = 'primary',
        size = 'md',
        variant = 'default',
        href,
        handleClick,
        leftIcon,
        rightIcon,
        className,
        leftComponent,
        hoverable = true,
        asButton = false,
        pressed,
        ariaLabel,
    } = props;

    const badge = (
        <Badge
            className={`${handleClick ? 'cursor-pointer' : ''} ${className} ${!hoverable ? 'pointer-events-none' : ''}`}
            id={asButton ? undefined : id}
            data-testid={asButton ? undefined : testId}
            theme={leftIconTheme(variant === 'compact' ? compactBadgeTheme : badgeTheme)}
            color={color}
            size={size}
            href={asButton ? undefined : href}
            onClick={asButton ? undefined : handleClick}
        >
            {leftIcon || leftComponent || label || rightIcon ? (
                <span className="flex flex-row items-center">
                    <TagIcon
                        icon={leftIcon}
                        color={color}
                        className={label || rightIcon ? 'mr-1' : undefined}
                        decorative={asButton}
                    />
                    {leftComponent?.condition && leftComponent.element}
                    {/* a button only takes phrasing content, so no <p> */}
                    <Text as={asButton ? 'span' : undefined} variant={labelVariants[size]}>
                        {label}
                    </Text>
                    <TagIcon icon={rightIcon} color={color} className="ml-1.5" decorative={asButton} />
                </span>
            ) : null}
        </Badge>
    );

    if (!asButton) {
        return badge;
    }

    return (
        <button
            type="button"
            id={id}
            data-testid={testId}
            aria-pressed={pressed}
            aria-label={ariaLabel}
            className={tagButtonClass}
            onClick={handleClick}
        >
            {badge}
        </button>
    );
};
